/*
和文と和文のあいだの半角の空きを、行内の部品 (太字・蛍光ペン・用語のリンクなど) の境目で詰める。
書き手は「ゆめさく が ==仲間== の集まり」のように、強調が効くよう部品の前後に空きを置く。
その空きは和文どうしの間では余計なので、表示では取り除く。和文と英数字の間の空きは残す。
部品を挟まない文の中ほどの空きは、書き手の決まりで置かれる「ゆめさく」の前後の空き 1 つ (「ゆめさく の」) だけ詰める。
ほかの和文どうしの空きは書き手が意味を込めて置いている (「山田 太郎」、「SOLD や 売切 のような」、「作品があります と表示される」) ので残す。
空きが 2 つ以上の所、改行、コード、kbd は触らない。
*/

// 和文の範囲。全角の英数字 (FF10-FF19、FF21-FF3A、FF41-FF5A) は英数字なので入れない
const CJK = /[\u3000-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uff00-\uff0f\uff1a-\uff20\uff3b-\uff40\uff5b-\uffef]/;
const SKIP = new Set(["pre", "code", "kbd", "svg", "style", "script", "textarea"]);
// 「ゆめさく」+ 半角の空き 1 つ + 和文、または和文 + 半角の空き 1 つ +「ゆめさく」。空きが 2 つ以上、タブ、改行は対象にしない
const WIDE = "\\u3000-\\u30ff\\u3400-\\u4dbf\\u4e00-\\u9fff\\uff00-\\uff0f\\uff1a-\\uff20\\uff3b-\\uff40\\uff5b-\\uffef";
const BRAND_GAP = new RegExp(`(?<=ゆめさく) (?=[${WIDE}])|(?<=[${WIDE}]) (?=ゆめさく)`, "g");

type HastNode = {
  type: string;
  tagName?: string;
  value?: string;
  children?: HastNode[];
};

function textOf(node: HastNode): string {
  if (node.type === "text") return node.value ?? "";
  return (node.children ?? []).map(textOf).join("");
}

/** 自分か子孫に code や pre などがあるか。あれば境目の空きは書き手の字のまま残す */
function holdsSkipped(node: HastNode): boolean {
  if (node.type !== "element") return false;
  if (SKIP.has(node.tagName ?? "")) return true;
  return (node.children ?? []).some(holdsSkipped);
}

function isCjk(ch: string | undefined): boolean {
  return ch !== undefined && ch !== "" && CJK.test(ch);
}

/** 隣の節の、境目に接する側の字。コードなどを含む部品は読まない (undefined) */
function edgeChar(node: HastNode | undefined, side: "first" | "last"): string | undefined {
  if (!node || holdsSkipped(node)) return undefined;
  const t = textOf(node);
  if (t === "") return undefined;
  return side === "first" ? t[0] : t[t.length - 1];
}

function isPlainElement(node: HastNode | undefined): boolean {
  return node?.type === "element" && !holdsSkipped(node);
}

function joinInChildren(children: HastNode[]): void {
  for (let i = 0; i < children.length; i++) {
    const node = children[i];

    // 空白だけのテキスト (「**日本** **語**」の間) は、両隣が和文で、どちらかが部品なら消す
    if (node.type === "text" && /^[ \t]+$/.test(node.value ?? "")) {
      const left = children[i - 1];
      const right = children[i + 1];
      if ((isPlainElement(left) || isPlainElement(right)) && isCjk(edgeChar(left, "last")) && isCjk(edgeChar(right, "first"))) {
        node.value = "";
      }
      continue;
    }

    if (!isPlainElement(node)) continue;
    const first = edgeChar(node, "first");
    const last = edgeChar(node, "last");

    const next = children[i + 1];
    if (next?.type === "text" && isCjk(last)) {
      const m = /^[ \t]+(?=\S)/.exec(next.value ?? "");
      if (m && isCjk((next.value ?? "")[m[0].length])) {
        next.value = (next.value ?? "").slice(m[0].length);
      }
    }
    const prev = children[i - 1];
    if (prev?.type === "text" && isCjk(first)) {
      const m = /(?<=\S)[ \t]+$/.exec(prev.value ?? "");
      if (m && isCjk((prev.value ?? "")[m.index - 1])) {
        prev.value = (prev.value ?? "").slice(0, m.index);
      }
    }
  }
}

function visit(node: HastNode): void {
  if (!node.children) return;
  if (node.type === "element" && SKIP.has(node.tagName ?? "")) return;
  joinInChildren(node.children);
  for (const child of node.children) {
    if (child.type === "text" && child.value) child.value = child.value.replace(BRAND_GAP, "");
    else visit(child);
  }
}

export function rehypeJoinCjkAcrossInline() {
  return (tree: HastNode) => {
    visit(tree);
  };
}
